import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { ReportingRunParametersRowMetadata } from "gen/model/reportingRunParametersRowMetadata";
import { useTranslation } from "react-i18next";
import Descriptions from "shared/components/descriptions/Descriptions";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { useBoolean } from "usehooks-ts";

interface HistoricalReportsModalProps {
  onCloseComplete(): void;
  reportType: string;
  ranOn: string;
  ranBy: string;
  parameters: ReportingRunParametersRowMetadata;
}

export const HistoricalReportsModal: React.FC<HistoricalReportsModalProps> = ({
  onCloseComplete,
  reportType,
  ranOn,
  ranBy,
  parameters,
}) => {
  const { value: isOpen, setFalse: close } = useBoolean(true);
  const { t } = useTranslation(["common", "reports"]);
  const {
    fund_legal_names,
    investor_names,
    start_date,
    end_date,
    effective_date,
    include_pending,
    include_draft,
    include_cancelled,
    fund_family_names,
  } = parameters;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {t("reports:reportModal.historicalModal.title", { reportType })}
        </ModalHeader>
        <ModalBody>
          <Text>
            {t("reports:reportModal.historicalModal.subtitle", {
              ranOn,
            })}
          </Text>
          <Box py={6}>
            <Descriptions responsiveColumn={true}>
              <Descriptions.Item label={t("reports:reportModal.creator")}>
                {ranBy}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:fundFamilies")}>
                {fund_family_names?.length
                  ? fund_family_names.join(", ")
                  : t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:fundEntities")}>
                {fund_legal_names?.length
                  ? fund_legal_names.join(", ")
                  : t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:investorEntities")}>
                {investor_names?.length
                  ? investor_names.join(", ")
                  : t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:startDate")}>
                {formatIsoDateToDisplay(start_date) ||
                  t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:endDate")}>
                {formatIsoDateToDisplay(end_date) ||
                  t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:effectiveDate")}>
                {formatIsoDateToDisplay(effective_date) ||
                  t("reports:reportModal.noneSelected")}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("reports:reportModal.includePending")}
              >
                {include_pending ? t("common:yes") : t("common:no")}
              </Descriptions.Item>
              <Descriptions.Item label={t("reports:reportModal.includeDraft")}>
                {include_draft ? t("common:yes") : t("common:no")}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("reports:reportModal.includeCancelled")}
              >
                {include_cancelled ? t("common:yes") : t("common:no")}
              </Descriptions.Item>
            </Descriptions>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
